import React, { useRef, useState } from "react"
import { motion } from "framer-motion"
import emailjs from '@emailjs/browser'
import { useToast } from "../hooks/use-toast"
import { Loader2, CheckCircle, XCircle, Send } from "lucide-react"

const containerVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0.17, duration: 0.7, type: "spring", stiffness: 60 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, type: "spring", stiffness: 90 } }
};

const Contact = () => {
  const { toast } = useToast()
  const form = useRef()
  const [isSending, setIsSending] = useState(false)

  const contact_info = [
    { logo: "mail", text: "omanyasa@yahoo.com" },
    { logo: "logo-whatsapp", text: "+263 779 050 634" },
    { logo: "location", text: "Newlands, Harare, Zimbabwe" },
  ];

  const sendEmail = async (e) => {
    e.preventDefault()
    if (isSending) return

    const formData = new FormData(form.current)
    const formProps = Object.fromEntries(formData)

    if (!formProps.user_name || !formProps.user_email || !formProps.message) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill in all required fields.",
      })
      return
    }

    setIsSending(true)
    let toastId

    try {
      toastId = toast({
        variant: "loading",
        title: "Sending message...",
        description: "Please wait while we send your message.",
        duration: 10000,
        action: (
          <div className="flex items-center space-x-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Sending...</span>
          </div>
        )
      })

      const result = await emailjs.sendForm(
        'service_g92j7cz',
        'template_brlrber',
        form.current,
        '6oBnk3QfeH_zESzWQ'
      )

      toast({
        id: toastId,
        variant: "success",
        title: "Message Sent!",
        description: "Thank you for your message. I'll get back to you soon!",
        duration: 5000,
        action: (
          <div className="flex items-center space-x-2 text-green-700">
            <CheckCircle className="h-5 w-5" />
            <span>Success</span>
          </div>
        )
      })

      form.current.reset()
    } catch (error) {
      console.error('EmailJS Error:', error)
      let errorMessage = 'Failed to send message. Please try again.'
      if (error.status === 412) {
        errorMessage = 'Validation failed. Please check your input and try again.'
      } else if (error.text) {
        errorMessage = error.text
      }
      toast({
        id: toastId,
        variant: "destructive",
        title: "Error",
        description: errorMessage,
        duration: 5000,
        action: (
          <div className="flex items-center space-x-2 text-red-700">
            <XCircle className="h-5 w-5" />
            <span>Failed</span>
          </div>
        )
      })
    } finally {
      setIsSending(false)
    }
  };

  return (
    <motion.section
      id="contact"
      className="py-24 relative overflow-hidden bg-slate-900/50"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.18 }}
      variants={containerVariants}
    >
      <div className="absolute inset-0 bg-grid opacity-20" />

      <motion.div
        className="absolute -top-24 left-1/3 w-96 h-96 bg-cyan-500/5 rounded-full blur-[120px]"
        animate={{ y: [0, 40, 0] }}
        transition={{ repeat: Infinity, duration: 11, ease: "easeInOut" }}
      />

      <div className="section-container relative z-10">
        <motion.div className="text-center mb-16" variants={itemVariants}>
          <motion.span
            className="inline-block px-4 py-1.5 mb-4 text-xs font-mono tracking-wider text-cyan-400 border border-cyan-500/30 rounded-full bg-cyan-500/5"
          >
            Contact
          </motion.span>
          <motion.h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Get In <span className="gradient-text">Touch</span>
          </motion.h2>
          <motion.p className="text-slate-400 text-base">
            Have a project in mind? Let's discuss how I can help.
          </motion.p>
        </motion.div>

        <motion.div
          className="max-w-5xl mx-auto glass-card rounded-2xl p-8"
          variants={containerVariants}
        >
          <div className="flex md:flex-row flex-col gap-8">
            <motion.form
              className="flex flex-col flex-1 gap-5"
              ref={form}
              onSubmit={sendEmail}
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <input
                type="text"
                required
                name="user_name"
                placeholder="Your Name"
                className="rounded-lg py-3 px-4 bg-slate-800/50 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/30 placeholder:text-slate-500 text-sm transition-all"
              />
              <input
                type="email"
                required
                name="user_email"
                placeholder="Your Email Address"
                className="rounded-lg py-3 px-4 bg-slate-800/50 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/30 placeholder:text-slate-500 text-sm transition-all"
              />
              <textarea
                placeholder="Your Message"
                required
                name="message"
                rows={6}
                className="rounded-lg py-3 px-4 bg-slate-800/50 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/30 placeholder:text-slate-500 text-sm transition-all resize-none"
              />
              <motion.button
                type="submit"
                disabled={isSending}
                className="px-8 py-3 rounded-full text-sm font-semibold shadow-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 transition-all text-white flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                whileHover={{ scale: isSending ? 1 : 1.03 }}
                whileTap={{ scale: isSending ? 1 : 0.97 }}
              >
                {isSending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send Message
                  </>
                )}
              </motion.button>
            </motion.form>

            <motion.div
              className="flex flex-col gap-5 justify-center md:pl-6 md:border-l border-slate-800"
              variants={containerVariants}
            >
              {contact_info.map((contact, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-4 group"
                  variants={itemVariants}
                  whileHover={{ x: 4 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <div className="w-10 h-10 flex items-center justify-center text-white bg-slate-800 rounded-full border border-slate-700 group-hover:border-cyan-500/30 transition-colors">
                    <ion-icon name={contact.logo} class="text-base"></ion-icon>
                  </div>
                  <p className="text-sm text-slate-300">
                    {contact.text}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Contact;
